import { createTheme, type ThemeOptions } from '@mui/material/styles';

export const rmtPrimaryColor = '#FFB657'; // hsl(34, 100%, 67% )
export const rmtSecondaryColor = '#003566'; // hsl(209, 100%, 20%)

export const drawerWidth = 280;

const themeStyles = {
  color: {
    rmtPrimaryColor,
    rmtSecondaryColor,
    primary: rmtPrimaryColor,
    secondary: rmtSecondaryColor,
    neutralAccent: '#E8EAF6',
    secondaryActionBg: '#f5f5f6', // Neutral gray for action button backgrounds
    secondaryActionBgHover: '#eaeaec', // Hover state for action button backgrounds
    elevatedSurface: '#f6f9fc',
    groundZeroSurface: '#f2f2f3',
    outlinedPaneBorder: '#e3e3e3',
    activeNavItem: '#dde8f8',
    activeNavItemHover: '#d0dff6',
    error: '#B3261E',
    info: '#ced9ee',
    infoTextColor: '#162c54',
    blue: '#003566',
    lightBlue: '#A2C1EB66',
    orange: '#FFB657',
    darkOrange: '#e29b3f',
    black: '#000000',
    grey: '#707070',
    white: '#ffffff',
    mute: 'rgba(0, 0, 0, 0.12)',
    lightGrey: '#f7f7f7',
    darkGrey: '#49454E',
    slateGrey: '#5c5c7a', // Neutral gray with indigo tint for action icons
    red: '#E3261F',
    paleRed: '#FEF2F2',
    paleRedOverlay: '#FEF2F238',
    lightRed: '#F9D4D2',
    darkRed: '#870601',
    green: '#78BF5D',
    paleGreen: '#F0FDF4',
    lightGreen: '#E4F2DF',
    yellow: '#FACD35',
  },
  fontFamily: {
    default: 'futura-pt',
    mono: 'SFMono-Regular,Menlo,Monaco,Consolas,Liberation Mono,Courier New,monospace',
  },
  fontSize: {
    /**
     * @deprecated
     */
    h1: '24px',
    /**
     * @deprecated
     */
    h2: '20px',
    /**
     * @deprecated
     */
    body: '16px',
    /**
     * @deprecated
     */
    small: '14px',
    /**
     * @deprecated
     */
    xsmall: '12px',
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '7xl': '72px',
  },
  /**
   * @deprecated
   */
  lineHeight: {
    display: '104px',
    h1: '31px',
    h2: '26px',
    body: '21px',
    small: '18px',
    xsmall: '16px',
    xs: '16px',
    sm: '18px',
    md: '21px',
    lg: '24px',
    xl: '26px',
    '2xl': '31px',
    '7xl': '104px',
  },
  drawerWidth: {
    default: `${drawerWidth}px`,
  },
  paperBorderRadius: {
    default: '12px',
  },
  shadow: {
    surface: `
      inset 0 1px 0 0 rgba(255,255,255,0.8),
      inset 0 -1px 0 0 rgba(0,0,0,0.01),
      0 0 0 1px rgba(0,0,0,0.02)
    `
      .trim()
      .replace(/\s+/g, ' '),
    inputInset: `
      inset 0px 0px 8px 2px rgba(255, 255, 255, 0.1),
      inset 0px 0px 0px 1px rgba(0, 0, 0, 0.04)
    `
      .trim()
      .replace(/\s+/g, ' '),
    buttonElevated: `
      -1px -1px 0px 0px rgba(255,255,255,0.8),
      0px 0px 0px 1px rgba(0,0,0,0.04)
    `
      .trim()
      .replace(/\s+/g, ' '),
    actionIcon: `
      -1px -1px 0px 0px rgba(255,255,255,0.9),
      1px 1px 1px 0px rgba(0,0,0,0.04),
      inset 0 0 0 0.5px rgba(0,0,0,0.06)
    `
      .trim()
      .replace(/\s+/g, ' '),
  },
};

export default themeStyles;

type ThemeKey = keyof typeof themeStyles;

const styleObjectToVariables = (styleKey: ThemeKey, styleValues: { [key: string]: any }) =>
  Object.keys(styleValues).reduce(
    (acc, key) => ({
      ...acc,
      [key]: `var(--${styleKey}-${key}, ${styleValues[key]})`,
    }),
    {},
  );

export const campaignManagerTheme: typeof themeStyles = Object.entries(themeStyles).reduce(
  (acc, [styleKey, styleValues]) => ({
    ...acc,
    [styleKey]: styleObjectToVariables(styleKey as ThemeKey, styleValues),
  }),
  {} as typeof themeStyles,
);

const muiThemeConfig: ThemeOptions = {
  palette: {
    contrastThreshold: 4.2,
    primary: {
      main: themeStyles.color.primary,
    },
    secondary: {
      main: themeStyles.color.secondary,
    },
    success: {
      main: themeStyles.color.green,
    },
    error: {
      main: themeStyles.color.error,
    },
    info: {
      main: themeStyles.color.info,
    },
  },
  typography: {
    fontFamily: [
      themeStyles.fontFamily.default,
      'ui-sans-serif',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      '"Noto Sans"',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
      '"Noto Color Emoji"',
    ].join(','),
    // Heading hierarchy with proper rhythm
    h1: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.35,
      letterSpacing: '0',
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.45,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    subtitle2: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: campaignManagerTheme.color.white,
          padding: '0px 36px 0px 24px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 400,
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'outlined' as const, color: 'inherit' as const },
          style: {
            color: themeStyles.color.grey,
            borderColor: 'rgba(0, 0, 0, 0.16)',
          },
        },
      ],
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '&.MuiLoadingButton-root, &.MuiButton-loading': {
              borderRadius: 8,
              pl: 2,
            },
            '.MuiButton-contained.MuiButton-colorInherit': {
              backgroundColor: `oklch(from ${themeStyles.color.groundZeroSurface} calc(l - 0.002) calc(c + 0.001) h)`,
            },
          }),
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
        sizeMedium: ({ theme }) =>
          theme.unstable_sx({
            fontSize: '14px',
            lineHeight: '20px',
          }),
        sizeSmall: ({ theme }) =>
          theme.unstable_sx({
            fontSize: '12px',
            lineHeight: '16px',
            px: 1,
          }),
      },
    },
    MuiLink: {
      styleOverrides: {
        button: {
          textDecoration: 'none',
          fontWeight: 500,
          fontFamily: 'inherit',
          fontSize: 'inherit',
          lineHeight: 'inherit',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          // Only apply neumorphic shadow for elevation 1 (cards/panes)
          // Higher elevations (dropdowns, modals) keep MUI's default shadows
          ...(ownerState.elevation === 1 && {
            boxShadow: themeStyles.shadow.surface,
          }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            borderRadius: 5,
            boxShadow: themeStyles.shadow.surface,
          }),
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        size: 'medium',
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            fontSize: '14px',
            lineHeight: '20px',
          }),
        // MUI v9 styles the outer buttons through the firstButton/lastButton
        // slots; there are no grouped{Variant} slots.
        firstButton: {
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
        lastButton: {
          borderTopRightRadius: 16,
          borderBottomRightRadius: 16,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFab: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
        margin: 'dense',
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            marginTop: 0.5,
          }),
        marginDense: ({ theme }) =>
          theme.unstable_sx({
            m: 0,
          }),
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        margin: 'dense',
      },
    },
    MuiInputBase: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
      },
      styleOverrides: {
        input: ({ theme }) =>
          theme.unstable_sx({
            py: 0,
          }),
        sizeSmall: ({ theme }) =>
          theme.unstable_sx({
            py: 0,
          }),
        multiline: ({ theme }) =>
          theme.unstable_sx({
            py: 0,
            pr: 0,
            '& textarea': {
              py: 0.75,
              pr: 1,
            },
          }),
      },
    },
    MuiInput: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
      },
    },
    MuiFilledInput: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: themeStyles.shadow.inputInset,
          backgroundColor: `oklch(from ${themeStyles.color.groundZeroSurface} calc(l - 0.02) calc(c + 0.001) h / 0.6)`,
          '&.Mui-disabled': {
            backgroundColor: `oklch(from ${themeStyles.color.groundZeroSurface} calc(l - 0.01) calc(c - 0.001) h / 0.44)`,
          },
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: `oklch(from ${themeStyles.color.groundZeroSurface} calc(l - 0.03) calc(c + 0.001) h / 0.66)`,
          },
          '&.Mui-focused': {
            backgroundColor: `oklch(from ${themeStyles.color.groundZeroSurface} calc(l - 0.02) calc(c + 0.001) h / 0.66)`,
          },
          '&:before, &.Mui-disabled:before, &:focus:before, &:hover:not(.Mui-disabled):before': {
            border: 'none',
          },
          '&:after': {
            border: 'none',
          },
        },
        input: ({ theme }) =>
          theme.unstable_sx({
            py: 0.75,
            '&:-internal-autofill-selected, &:-internal-autofill-selected:focus': {
              borderRadius: 2,
            },
          }),
        multiline: ({ theme }) =>
          theme.unstable_sx({
            py: 0,
            pr: 0,
            '& textarea': {
              py: 0.75,
              pr: 1,
            },
          }),
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          background: themeStyles.color.white,
        },
        input: ({ theme }) =>
          theme.unstable_sx({
            py: 0.75,
            '&:-internal-autofill-selected, &:-internal-autofill-selected:focus': {
              borderRadius: 2,
            },
          }),
        multiline: ({ theme }) =>
          theme.unstable_sx({
            py: 0,
            pr: 0,
            '& textarea': {
              py: 0.75,
              pr: 1,
            },
          }),
      },
    },
    MuiSelect: {
      defaultProps: {
        margin: 'dense',
        size: 'small',
      },
      styleOverrides: {
        select: ({ theme }) =>
          theme.unstable_sx({
            fontSize: 14,
            lineHeight: '20px',
            minHeight: '20px',
          }),
        filled: ({ theme }) =>
          theme.unstable_sx({
            py: 0.75,
          }),
        outlined: ({ theme }) =>
          theme.unstable_sx({
            py: 0.75,
          }),
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            '& .MuiAutocomplete-tag': {
              my: 0,
            },
            '& .MuiInput-root.MuiInputBase-sizeSmall .MuiInput-input': {
              fontSize: 14,
              py: '1px',
            },
            '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
              fontSize: 14,
              py: 0.5,
            },
            '& .MuiFilledInput-root.MuiInputBase-sizeSmall': {
              fontSize: 14,
              py: 0.5,
            },
            '& .MuiFilledInput-root.MuiInputBase-hiddenLabel.MuiInputBase-sizeSmall .MuiAutocomplete-input':
              {
                py: 0.25,
              },
            '& .MuiFilledInput-root': {
              pt: 0.5,
            },
            '& .MuiInputBase-root.Mui-error': {
              border: '1px solid',
              borderColor: themeStyles.color.error,
            },
          }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            fontSize: 14,
          }),
      },
    },
    MuiInputLabel: {
      defaultProps: {
        margin: 'dense',
      },
    },
    MuiRadio: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        margin: 'dense',
        // Deliberately no htmlInput default here. It used to read
        // `inputProps: { style: { fontSize: 12 } }`, and the v9 codemod
        // faithfully rewrote it to slotProps.htmlInput — which silently turned
        // it ON. In v7 TextField built its slots as
        // `{ htmlInput: inputPropsProp, ...slotProps }`, so any component
        // passing slotProps.htmlInput replaced the key wholesale and discarded
        // this default; our TextField wrapper always passes it, so this was
        // dead for every wrapper field. v9 deep-merges defaultProps instead, so
        // it started applying — and as an inline `style` it outranks the
        // wrapper's sx, shrinking every input from 14px to 12px.
        // styleOverrides.root below already provides the 12px baseline that
        // raw MuiTextField usages inherit, so nothing needs it here.
      },
      styleOverrides: {
        root: {
          fontSize: 12,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ theme }) =>
          theme.unstable_sx({
            mb: 0,
          }),
        markLabel: ({ theme }) =>
          theme.unstable_sx({
            fontSize: 10,
            top: 20,
          }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        colorSecondary: {
          // Muted gray for secondary icons (instead of brand secondary color)
          color: themeStyles.color.slateGrey,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
};

export function buildTheme(primary?: string, secondary?: string) {
  const theme = createTheme(muiThemeConfig);

  return createTheme(theme, {
    palette: {
      primary: theme.palette.augmentColor({
        color: {
          main: primary || rmtPrimaryColor,
        },
        name: 'primary',
      }),
      'secondary-project': theme.palette.augmentColor({
        color: {
          main: secondary || rmtSecondaryColor,
        },
        name: 'secondary-project',
      }),
    },
  });
}
